// ============================================================================
// DISCOUNTS API - Frontend
// API client para gestión de tipos de descuento
// ============================================================================

import { api } from './client';

// ============================================================================
// TYPES
// ============================================================================

export type DiscountType = 'PERCENTAGE' | 'FIXED';

export interface Discount {
  id: string;
  code: string;
  name: string;
  description: string | null;
  type: DiscountType;
  value: number;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  sitesCount: number;
  sites?: { id: string; code: string; name: string }[];
}

export interface DiscountSelect {
  id: string;
  code: string;
  name: string;
  type: DiscountType;
  value: number;
}

export interface CreateDiscountDto {
  code: string;
  name: string;
  description?: string;
  type: DiscountType;
  value: number;
  sortOrder?: number;
}

export interface UpdateDiscountDto {
  code?: string;
  name?: string;
  description?: string;
  type?: DiscountType;
  value?: number;
  sortOrder?: number;
}

export interface DiscountQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  includeInactive?: boolean;
  type?: DiscountType;
  sortBy?: 'name' | 'code' | 'value' | 'createdAt' | 'sortOrder';
  sortOrder?: 'asc' | 'desc';
}

export interface DiscountLinkedSite {
  id: string;
  code: string;
  name: string;
  isLinked: boolean;
}

export interface UpdateDiscountSitesDto {
  siteIds: string[];
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// ============================================================================
// HELPERS
// ============================================================================

export const DISCOUNT_TYPE_LABELS: Record<DiscountType, string> = {
  PERCENTAGE: 'Porcentaje',
  FIXED: 'Cantidad fija',
};

export const formatDiscountValue = (type: DiscountType, value: number): string => {
  if (type === 'PERCENTAGE') {
    return `${value}%`;
  }
  return `${value.toFixed(2)} €`;
};

// ============================================================================
// API FUNCTIONS
// ============================================================================

export const discountsApi = {
  /**
   * Crear un nuevo descuento
   */
  create: async (data: CreateDiscountDto): Promise<Discount> => {
    return api.post<Discount>('/discounts', data);
  },

  /**
   * Obtener todos los descuentos (paginado)
   */
  getAll: async (params?: DiscountQueryParams): Promise<PaginatedResponse<Discount>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.includeInactive) searchParams.append('includeInactive', 'true');
    if (params?.type) searchParams.append('type', params.type);
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    
    const query = searchParams.toString();
    return api.get<PaginatedResponse<Discount>>(`/discounts${query ? `?${query}` : ''}`);
  },

  /**
   * Obtener descuentos para dropdown
   */
  getForSelect: async (): Promise<DiscountSelect[]> => {
    return api.get<DiscountSelect[]>('/discounts/select');
  },

  /**
   * Obtener descuentos para dropdown filtrados por sede
   */
  getForSelectBySite: async (siteId: string): Promise<DiscountSelect[]> => {
    return api.get<DiscountSelect[]>(`/discounts/select/site/${siteId}`);
  },

  /**
   * Obtener un descuento por ID
   */
  getById: async (id: string): Promise<Discount> => {
    return api.get<Discount>(`/discounts/${id}`);
  },

  /**
   * Actualizar un descuento
   */
  update: async (id: string, data: UpdateDiscountDto): Promise<Discount> => {
    return api.patch<Discount>(`/discounts/${id}`, data);
  },

  /**
   * Desactivar un descuento
   */
  deactivate: async (id: string): Promise<void> => {
    await api.delete(`/discounts/${id}`);
  },

  /**
   * Reactivar un descuento
   */
  reactivate: async (id: string): Promise<void> => {
    await api.patch(`/discounts/${id}/reactivate`);
  },

  /**
   * Obtener sedes vinculadas a un descuento
   */
  getLinkedSites: async (id: string): Promise<DiscountLinkedSite[]> => {
    return api.get<DiscountLinkedSite[]>(`/discounts/${id}/sites`);
  },

  /**
   * Actualizar sedes de un descuento
   */
  updateSites: async (
    id: string,
    data: UpdateDiscountSitesDto
  ): Promise<{ success: boolean; message: string }> => {
    return api.patch<{ success: boolean; message: string }>(
      `/discounts/${id}/sites`,
      data
    );
  },
};

export default discountsApi;
